import React, { useEffect, useState } from "react";
import { X, Sparkles, AlertTriangle } from "lucide-react";
import axios from "axios";
import PersonaStep from "./PersonaStep";
import { useUserAuth } from "../lib/userAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PersonaPromptModal — Phase 1 soft-prompt for accounts that pre-date
 * the persona requirement. Mounted globally inside the authed app
 * shell. Behavior matrix:
 *   • Mounts ONLY when account.persona_type is null/undefined.
 *   • Dismissable up to DISMISS_LIMIT (default 3) times.
 *   • Becomes mandatory (no skip button + close icon hidden) on the
 *     attempt past the limit.
 *   • Records dismissal counter on the server so closing in one
 *     browser doesn't reset progress in another.
 */
export default function PersonaPromptModal() {
  const { account, refresh, client } = useUserAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dismissLimit, setDismissLimit] = useState(3);

  // Decide whether to show. Avoid surfacing on `/admin/*` and during
  // the signup flow itself.
  useEffect(() => {
    if (!account) return;
    if (account.persona_type) return;
    if (typeof window !== "undefined" &&
        (window.location.pathname.startsWith("/admin") ||
         window.location.pathname.startsWith("/signin") ||
         window.location.pathname.startsWith("/signup") ||
         window.location.pathname.startsWith("/help") ||
         window.location.pathname.startsWith("/trends") ||
         window.location.pathname.startsWith("/account/settings") ||
         window.location.pathname.startsWith("/account/inventory") ||
         window.location.pathname.startsWith("/unsubscribe"))) {
      return;
    }
    setOpen(true);
  }, [account]);

  // Pull DISMISS_LIMIT once.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/account/persona/options`).then((r) => {
      if (alive && r.data?.dismiss_limit) setDismissLimit(r.data.dismiss_limit);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!open || !account || account.persona_type) return null;

  const dismissed = account.persona_prompt_dismissed_count || 0;
  const mandatory = dismissed >= dismissLimit;

  const handleSubmit = async (payload) => {
    setBusy(true); setError("");
    try {
      await client.post("/account/persona", payload);
      await refresh();
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    setBusy(true);
    try {
      await client.post("/account/persona/dismiss");
      await refresh();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Tell us about you"
      data-testid="persona-prompt-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      style={{ background: "rgba(0,4,10,0.78)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="relative w-full max-w-[680px] rounded-2xl border p-6 sm:p-8"
        style={{
          background: "#050B14",
          borderColor: "rgba(122,231,242,0.28)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
        }}
      >
        {!mandatory && (
          <button
            type="button"
            onClick={handleSkip}
            disabled={busy}
            aria-label="Remind me later"
            data-testid="persona-prompt-close-btn"
            className="absolute top-4 right-4 p-1 rounded-full transition-colors"
            style={{ color: "rgba(255,255,255,0.6)" }}
          >
            <X size={18} />
          </button>
        )}

        <div
          className="qt-mono text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2"
          style={{ color: "#7AE7F2" }}
        >
          <Sparkles size={13} />
          <span>{mandatory ? "One more thing" : "Tell us about you"}</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          {mandatory
            ? "Help us tailor QorTrace before you continue"
            : "Help us tailor QorTrace to your work"}
        </h2>
        <p className="text-sm text-white/70 mb-5">
          We use this to personalize onboarding, docs, and support — nothing more.
          {mandatory ? " This is required to continue." : " You can update it anytime in account settings."}
        </p>

        {mandatory && (
          <div
            className="px-3 py-2 mb-5 rounded text-xs flex items-start gap-2"
            style={{
              background: "rgba(255,205,86,0.06)",
              border: "1px solid rgba(255,205,86,0.3)",
              color: "rgba(255,205,86,0.95)",
            }}
            data-testid="persona-prompt-mandatory-banner"
          >
            <AlertTriangle size={13} style={{ marginTop: 2 }} />
            <div>
              You've skipped this {dismissed} times. We need a bit of context to keep
              showing you the right pages and avoid bombarding you with the wrong
              sales emails.
            </div>
          </div>
        )}

        <PersonaStep
          testid="persona-prompt"
          submitLabel="Save"
          busy={busy}
          error={error}
          mandatory={mandatory}
          onSubmit={handleSubmit}
          onSkip={mandatory ? undefined : handleSkip}
        />
      </div>
    </div>
  );
}
